/**
 * Stripe integration — handles Checkout sessions for care plan payments.
 * Uses Stripe test mode (sandbox) for development.
 */
import { loadStripe, type Stripe } from '@stripe/stripe-js';

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

let stripePromise: Promise<Stripe | null> | null = null;

/**
 * Load Stripe.js (cached singleton)
 */
export function getStripe(): Promise<Stripe | null> {
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
}

/**
 * Create the draft care plan server-side BEFORE checkout. The webhook flips
 * it to 'paid' and the fulfillment worker builds it — so a closed tab or lost
 * localStorage can never orphan a paid session.
 */
export async function createDraftPlan(params: {
  intake: Record<string, unknown>;
  plan_name?: string;
  latitude: number;
  longitude: number;
  location_label: string;
  location_state?: string;
  search_radius_miles?: number;
  insurance_status?: string;
  plan_year?: number;
  consolidation_level?: number;
  email?: string | null;
  family_member_name?: string | null;
  anonymous_user_id?: string | null;
}): Promise<{ plan_id: string; share_token: string }> {
  const resp = await fetch('/api/create-draft-plan', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Failed to create draft plan');
  }
  return resp.json();
}

/**
 * Create a Stripe Checkout Session for a care plan via our API server,
 * then redirect to Stripe's hosted checkout page. Pass planId so the
 * payment is tied to the draft plan (webhook fulfillment).
 */
export async function createCarePlanCheckout(params: {
  successUrl: string;
  cancelUrl: string;
  planId?: string;
  email?: string | null;
}): Promise<string> {
  const resp = await fetch('/api/create-checkout-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      success_url: params.successUrl,
      cancel_url: params.cancelUrl,
      plan_id: params.planId,
      email: params.email || undefined,
    }),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Failed to create checkout session');
  }

  const { url } = await resp.json();
  return url;
}

/**
 * Trigger the optimizer for a care plan via the API server.
 */
export async function triggerOptimizer(planId: string): Promise<void> {
  const resp = await fetch('/api/optimize', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan_id: planId }),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Failed to trigger optimizer');
  }
}

export const formatPrice = (cents: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

export const dollarsToCents = (dollars: number): number => {
  return Math.round(dollars * 100);
};

export const centsToDollars = (cents: number): number => {
  return cents / 100;
};
